import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import "./UserInboxPage.css";
import notificationSound from "../assets/notification.mp3";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ConsentRequestsCard from "../components/ConsentRequestsCard";

const MAX_PREVIEW_LENGTH = 220;

const UserInboxPage = () => {
  const [messages, setMessages] = useState([]);
  const [newMessageIds, setNewMessageIds] = useState(new Set());
  const [expandedIds, setExpandedIds] = useState(new Set());
  const user = getAuth().currentUser;
  const navigate = useNavigate();

  const getSeenMessageIds = () =>
    JSON.parse(localStorage.getItem("seenMessageIds") || "[]");

  const setSeenMessageIds = (ids) =>
    localStorage.setItem("seenMessageIds", JSON.stringify([...new Set(ids)]));

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (!user) return;
    const seenIds = new Set(getSeenMessageIds());
    const updatedSeen = new Set(seenIds);
    localStorage.setItem("hasNewMessage", "false");

    const allMessagesQuery = query(
      collection(db, "messages"),
      where("to", "in", ["all", user.uid]),
      orderBy("timestamp", "desc")
    );

    let allMessages = [];
    let personalMessages = [];

    const mergeAndSetMessages = () => {
      const merged = [...allMessages, ...personalMessages].sort(
        (a, b) => b.timestamp?.seconds - a.timestamp?.seconds
      );
      setMessages(merged);
    };

    const handleNewMessages = (snapshot) => {
      const newIds = snapshot
        .docChanges()
        .filter((change) => change.type === "added")
        .map((change) => change.doc.id);

      if (newIds.length > 0) {
        setNewMessageIds((prev) => new Set([...prev, ...newIds]));
        try {
          const audio = new Audio(notificationSound);
          audio.play().catch(() => {
            console.log("Audio autoplay prevented (user hasn't interacted with page yet)");
          });
        } catch (error) {
          console.log("Audio play failed:", error);
        }
      }
    };

    const unsubscribe = onSnapshot(allMessagesQuery, (snapshot) => {
      const newMsgs = [];
      const updatedIds = [];

      const fetchedMessages = snapshot.docs.map((doc) => {
        const data = { id: doc.id, ...doc.data() };
        if (!seenIds.has(doc.id)) {
          newMsgs.push(data);
          updatedSeen.add(doc.id);
          updatedIds.push(doc.id);
        }
        return data;
      });

      if (newMsgs.length > 0) {
        setMessages(fetchedMessages);
        setNewMessageIds(new Set(updatedIds));
        try {
          const audio = new Audio(notificationSound);
          audio.play().catch(() => {
            console.log("Audio autoplay prevented (user hasn't interacted with page yet)");
          });
        } catch (error) {
          console.log("Audio play failed:", error);
        }
        localStorage.setItem("hasNewMessage", "true");
      } else {
        setMessages(fetchedMessages);
      }
    });

    return () => {
      const finalSeen = Array.from(updatedSeen);
      setSeenMessageIds(finalSeen);
      localStorage.setItem("hasNewMessage", "false");
      unsubscribe();
    };
  }, [user]);

  return (
    <div className="inbox-container">
      <div className="inbox-header">
        <FaArrowLeft className="inbox-back-icon" onClick={() => navigate(-1)} />
        <h2 className="inbox-title">Inbox</h2>
      </div>
      <ConsentRequestsCard />
      {messages.length === 0 ? (
        <div className="inbox-empty">No messages yet</div>
      ) : (
        <div className="inbox-message-list">
          {messages.map((msg) => {
            const isUnseen = !getSeenMessageIds().includes(msg.id);
            const content = msg.content || "";
            const isLong = content.length > MAX_PREVIEW_LENGTH;
            const isExpanded = expandedIds.has(msg.id);
            const displayText =
              isLong && !isExpanded
                ? `${content.slice(0, MAX_PREVIEW_LENGTH).trim()}…`
                : content;

            // ✅ NEW: evolve notifications are tappable and open the idea
            const isEvolve = msg.type === "evolve" && msg.ideaId;

            return (
              // ✅ CHANGED: added onClick + pointer cursor for evolve cards
              <div
                key={msg.id}
                className="inbox-message-card"
                onClick={isEvolve ? () => navigate(`/view-idea/${msg.ideaId}`) : undefined}
                style={isEvolve ? { cursor: "pointer" } : undefined}
              >
                {isUnseen && (
                  <span className="inbox-unread-seal" aria-label="Unread message">
                    <span className="inbox-sr-only">Unread</span>
                  </span>
                )}

                <p className="inbox-message-text">{displayText}</p>

                {isLong && (
                  <button
                    type="button"
                    className="inbox-read-more-btn"
                    // ✅ CHANGED: stopPropagation so "Read more" doesn't trigger the card tap
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpanded(msg.id);
                    }}
                  >
                    {isExpanded ? "Show less" : "Read more"}
                  </button>
                )}

                <span className="inbox-timestamp">
                  {msg.timestamp?.seconds
                    ? new Date(msg.timestamp.seconds * 1000).toLocaleString()
                    : "Sending..."}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserInboxPage;